import type { CampaignDraftCreatorItem } from '@/entities/campaign';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import {
  type CreatorCsvPreviewState,
  buildCreatorBulkPastePreview,
  warnUnsupportedCampaignPlatforms,
  withFallbackCreatorRow,
} from '../../../../hooks';

type UseCampaignCreatorBulkPasteArgs = {
  campaignItemSlotLimit: number;
  draftCreatorItems: CampaignDraftCreatorItem[];
  remainingSlots: number;
  resetCreatorValidation: () => void;
  setCreatorItems: Dispatch<SetStateAction<CampaignDraftCreatorItem[]>>;
};

export function useCampaignCreatorBulkPaste({
  campaignItemSlotLimit,
  draftCreatorItems,
  remainingSlots,
  resetCreatorValidation,
  setCreatorItems,
}: UseCampaignCreatorBulkPasteArgs) {
  const getCreatorBulkPastePreview = useCallback(
    (text: string) => {
      if (!text.trim()) return undefined;

      return buildCreatorBulkPastePreview({
        text,
        existingRows: draftCreatorItems,
        availableSlots: remainingSlots,
        itemLimit: campaignItemSlotLimit,
      });
    },
    [campaignItemSlotLimit, draftCreatorItems, remainingSlots],
  );

  const applyCreatorBulkPaste = useCallback(
    (preview: CreatorCsvPreviewState) => {
      const unsupportedPlatformWarning = warnUnsupportedCampaignPlatforms(
        preview.unsupportedPlatformCount,
      );
      if (preview.summary.added === 0) return;
      resetCreatorValidation();
      setCreatorItems(withFallbackCreatorRow(preview.rows));
      if (unsupportedPlatformWarning) {
        toast.warning(unsupportedPlatformWarning);
      }
    },
    [resetCreatorValidation, setCreatorItems],
  );

  return {
    applyCreatorBulkPaste,
    getCreatorBulkPastePreview,
  };
}
